import React, { memo, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import { IQuillFormats } from "modules/quill/use-quill";
import colors from "app/colors";
import { FontColorIcon } from "modules/icons";

const FONT_COLORS = [
  "#000000",
  "#848484",
  "#D6D6D6",
  "#FFFFFF",
  "#FF0000",
  "#00BF33",
  "#FFF200",
  "#FFA500",
  "#0066FF",
  "#A102D5",
];

const COLORS_WITH_BACKGROUND = ["#FFFFFF", "#FFF200"];

interface IProps {
  format: (name: string, value: any) => void;
  activeFormats: IQuillFormats;
}

const ComposeQuillTextColorSelector = ({ format, activeFormats }: IProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isSelected = useMemo(
    () =>
      !!activeFormats.color &&
      typeof activeFormats.color === "string" &&
      FONT_COLORS.includes(activeFormats.color.toUpperCase()),
    [activeFormats],
  );

  const color = useMemo(
    () =>
      !!activeFormats.color &&
      typeof activeFormats.color === "string" &&
      FONT_COLORS.includes(activeFormats.color.toUpperCase())
        ? activeFormats.color
        : colors.gray40,
    [activeFormats],
  );

  const bg = useMemo(() => {
    if (typeof color === "string") {
      if (isSelected && COLORS_WITH_BACKGROUND.includes(color.toUpperCase())) {
        return colors.gray30;
      }
    }
    return colors.transparent;
  }, [color, isSelected]);

  const onClick = useCallback(
    (color: string) => {
      format("color", color);
      onClose();
    },
    [format, onClose],
  );

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <Button
          variant="hover"
          size="sm"
          aria-label="Format text color"
          tabIndex={-1}
          px={0}
        >
          <Box
            bg={bg}
            w={5}
            h={5}
            borderRadius={4}
            display="flex"
            justifyContent="center"
          >
            <FontColorIcon boxSize={4} fill={color} bg={bg} borderRadius={4} />
          </Box>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="auto" p={2}>
        <PopoverArrow />
        <SimpleGrid columns={5} spacing={1}>
          {FONT_COLORS.map((color) => (
            <Box
              key={color}
              w={6}
              h={6}
              bg={color}
              borderWidth={1}
              borderColor={colors.gray30}
              borderRadius="4px"
              cursor="pointer"
              _hover={{ transform: "scale(1.1)" }}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                onClick(color);
              }}
            />
          ))}
        </SimpleGrid>
      </PopoverContent>
    </Popover>
  );
};

export default memo(ComposeQuillTextColorSelector);
